import * as THREE from 'three/webgpu';
import type { AssetHandle, AssetService } from '../assets';
import {
  compose,
  createRuntime,
  createSlot,
  evaluate,
  slotPlay,
  slotStep,
  type AnimParams,
  type GraphRuntime,
  type SlotState,
} from '../anim/graph';
import { advancePhase, blendedStride, clipTime01, cycleHz } from '../anim/phaseSync';
import { PLAYER_GRAPH, ROLL_TUCK_CLIP, rollTuckTime } from '../anim/playerGraph';
import type { FeedbackEvent } from '../sim/feedback';
import { CLIP_META, metaFor } from './clipMeta';
import { PoseController } from './poseController';

/**
 * The real character. Pose flow per frame: AnimGraph (pure) → phase-sync + one-shot bridge →
 * mixer weights → mixer.update → PoseController (lean/bank + hand-IK API).
 *
 * Clip surgery happens at LOAD, before the first clipAction() (bindings cache there):
 *  - Hips.position rebase (the jump family carries ~1 m of baked vertical root motion; fall/
 *    float rest ~1 m/0.2 m above ground — physics owns vertical, so we rebase or flatten).
 *  - Loop modes: measured non-looping clips get LoopOnce + clampWhenFinished (the hold-jump
 *    glitch was jump_start looping its baked crouch + 1 m snap every 1.4 s).
 *  - One-shots restart via reset() + time = trimStartS on weight 0→>0 entry (trim skips the
 *    baked anticipation crouch / airborne descent head).
 * Locomotion clips are phase-owned: timeScale 0, .time written from one shared stride phase —
 * foot speed equals ground speed by construction, and blended footfalls coincide.
 */
/**
 * ── ⭐ P1 · THE CHARACTER IS A LIBRARY ASSET NOW (ADR-0003) ──────────────────────────────────────
 *
 * This table was `CLIP_FILES` — eleven filenames under a hard-coded `/assets/character/`, with no
 * version, no content hash, no byte budget and no way to release any of it. One column changed:
 * the value is a CATALOG ID. Everything the animation layer knows is unchanged, because the key is
 * unchanged — `PLAYER_GRAPH`, `clipMeta.json`, the phase-sync tables and `PoseController` all
 * speak in these NAMES and have never known where a clip came from.
 *
 * ⚠️ THE KEY IS THE GRAPH'S NAME AND THE VALUE IS THE ID, and it is written as a MAP rather than
 * as the brief's flat id list on purpose: deriving `fall_loop` from `player-clip-fall-loop` is a
 * SECOND naming rule (kebab → snake, minus a prefix) living nowhere, and the first time a clip is
 * named after the motion rather than after the file it would silently stop resolving. The graph's
 * vocabulary and the catalog's are two namespaces; this is the one place they meet.
 *
 * Five more clips exist in the catalog and are deliberately NOT here — `player-clip-descend`,
 * `-dodge`, `-fall`, `-jump-start`, `-wall-jump`. They were on disk and unfetched before this
 * change too (research 02 §1.1); publishing them costs nothing and acquiring them would cost 350 KB
 * of boot for curves the graph never plays.
 */
export const PLAYER_BODY_ASSET = 'player-body-blue-001';

export const CLIP_ASSETS: Record<string, string> = {
  idle: 'player-clip-idle',
  walk: 'player-clip-walk',
  run: 'player-clip-run',
  run_boost: 'player-clip-run-boost',
  rise: 'player-clip-rise', // A_Jumping_Up — post-trim length 0.3666 s ≈ physics apex (natural 1× fit)
  jump_run_start: 'player-clip-jump-run-start',
  fall_loop: 'player-clip-fall-loop', // A_Falling — clean airborne loop at ground-relative height
  fall_run: 'player-clip-fall-run',
  land: 'player-clip-land',
  land_run: 'player-clip-land-run',
  float: 'player-clip-float',
};

/**
 * ── ⭐ THE `Rig` / `rig` MISMATCH, FIXED AT BIND TIME AND ONLY WHERE IT IS PROVABLY A RENAME ─────
 *
 * MEASURED, on the sixteen published clips: the body's armature root node is `Rig`, every clip's
 * is `rig`, and three's `PropertyBinding` does not fold case — so `rig.position/quaternion/scale`
 * have failed to bind on every boot this project has had, printing three
 * `PropertyBinding: No target node found` warnings per clip (research 02 §1.3, and the note in
 * `tests/exploration/axonRig.test.ts:26-27`).
 *
 * ⚠️ THE FIX IS DATA-DRIVEN, NOT A CASE FOLD. `rig.json` — the body's own sidecar, emitted by
 * `cook:asset` — carries `skeletonRoot: "Rig"` and `clipSkeletonRoot: "rig"`, which is a NAME MAP
 * and not a guess. A blanket case-insensitive match would be a different function: on a rig
 * carrying both spellings it would bind one node's curves onto another's, silently, and look
 * exactly like this working.
 *
 * So the remap runs only when all four legs hold — both names present in the sidecar, they differ,
 * the body HAS a node spelled the sidecar's way, and the body does NOT have one spelled the clips'
 * way (which would make the map ambiguous, and the clip's own spelling the truthful one). It warns
 * ONCE saying what it did.
 *
 * ⚠️ AND IT IS SAFE TO BIND THEM, WHICH IS A MEASUREMENT AND NOT AN ASSUMPTION. Across all sixteen
 * published clips every `rig.*` track is CONSTANT — translation `[0,0,0]`, rotation `[0,0,0,1]`,
 * scale `[100,100,100]` at every key — against a body whose `Rig` node is
 * `scale [100, 99.9999924, 99.9999924]`. Binding them therefore moves the pose by a relative
 * 7.6e-8 (sub-nanometre on a 1.3 m body) and removes 48 warnings a boot. Had a single track varied,
 * the honest move would have been to leave the binding broken and say so.
 *
 * ⚠️ NAMES ARE COMPARED THROUGH three's OWN `PropertyBinding.sanitizeNodeName`, WHICH IS THE
 * FUNCTION THAT WILL DO THE BINDING. It strips `[ ] . : /` and maps whitespace to `_`, so
 * `VB LeftFoot_LeftFoot` reaches the runtime as `VB_LeftFoot_LeftFoot`; comparing against the raw
 * glTF spelling is the L5 sidecar bug in a different subsystem. Taken from `three` rather than
 * from `world/collisionFile.ts`'s shared copy for a BUNDLE reason as well as a correctness one:
 * that module is asserted LAZY-ONLY by `checkBundle` (c1b) and this file is eager, so importing it
 * here drags the whole `.col` reader into the entry chunk — measured, it failed that row.
 */
const sanitizeNodeName = (name: string): string => THREE.PropertyBinding.sanitizeNodeName(name);
export interface SkeletonRootMap {
  /** The body's spelling (`rig.json.skeletonRoot`). */
  body: string;
  /** The clips' spelling (`rig.json.clipSkeletonRoot`). */
  clip: string;
}

/** `null` — no remap — unless every leg of the guard above holds. Never throws. */
export function skeletonRootMap(root: THREE.Object3D, rig: Record<string, unknown>): SkeletonRootMap | null {
  const bodyName = typeof rig['skeletonRoot'] === 'string' ? rig['skeletonRoot'] : '';
  const clipName = typeof rig['clipSkeletonRoot'] === 'string' ? rig['clipSkeletonRoot'] : '';
  if (bodyName === '' || clipName === '') return null;
  const to = sanitizeNodeName(bodyName);
  const from = sanitizeNodeName(clipName);
  if (to === from) return null;
  const names = new Set<string>();
  root.traverse((o) => names.add(sanitizeNodeName(o.name)));
  // The destination must exist, and the source must NOT: a body carrying both spellings means the
  // clip's own name resolves, and rewriting it would bind one node's curves onto another's.
  if (!names.has(to) || names.has(from)) return null;
  return { body: to, clip: from };
}

/**
 * Rewrite `<clipRoot>.<property>` → `<bodyRoot>.<property>` on a clip's tracks. Returns how many
 * tracks moved. The clip must already be a CLONE (see `load` — the source is shared).
 */
export function remapSkeletonRoot(clip: THREE.AnimationClip, map: SkeletonRootMap): number {
  let moved = 0;
  for (const track of clip.tracks) {
    const dot = track.name.indexOf('.');
    if (dot < 0) continue;
    if (sanitizeNodeName(track.name.slice(0, dot)) !== map.clip) continue;
    track.name = `${map.body}${track.name.slice(dot)}`;
    moved++;
  }
  return moved;
}

/** worldY = -localZ × 100 for Hips.position (Rig scale 100, Root basis maps localZ → -worldY). */
const WORLD_Y_PER_LOCAL_Z = -100;

function surgeonHipsTrack(clip: THREE.AnimationClip, clipName: string): void {
  const meta = metaFor(clipName);
  const op = meta?.hipsYOp ?? 'none';
  if (op === 'none') return;
  const track = clip.tracks.find((t) => t.name === 'Hips.position');
  if (!track) {
    console.warn(`[anim] ${clipName}: no Hips.position track for hipsYOp=${op}`);
    return;
  }
  const values = track.values as Float32Array;
  const restZ = CLIP_META.hipsIdleRestLocal[2];
  for (let i = 0; i < values.length; i += 3) {
    if (op === 'flattenToRest') {
      values[i + 2] = restZ;
    } else if (op === 'subtractConstant' && meta?.hipsRestOffsetM !== undefined) {
      // Subtract ΔY meters of world height == add ΔY/|WORLD_Y_PER_LOCAL_Z| to local Z.
      values[i + 2]! += meta.hipsRestOffsetM / Math.abs(WORLD_Y_PER_LOCAL_Z);
    }
  }
}

export class CharacterView {
  readonly root: THREE.Object3D;
  readonly pose = new PoseController();
  private readonly mixer: THREE.AnimationMixer;
  private readonly actions = new Map<string, THREE.AnimationAction>();
  private readonly durations = new Map<string, number>();
  private readonly prevWeights = new Map<string, number>();
  private readonly graphRt: GraphRuntime = createRuntime();
  private readonly slot: SlotState = createSlot();
  private locomotionPhase = 0;
  /** The library handles this view holds — released by `dispose()` and by nothing else. */
  private handles: AssetHandle[] = [];
  /** Materials this view MINTED (never the shared source's — see `dispose`). */
  private owned: THREE.Material[] = [];
  private disposed = false;
  /** Render-scale multiplier applied to authored stride lengths (mesh scale × stride trim). */
  strideScale = 1;
  /** Cadence cap (cycles/s) — above it, foot slide grows proportionally instead of leg speed. */
  maxCycleHz = 2.3;
  /** Per-surface cadence multiplier (ice skate-slip etc.) — set by the host per frame. */
  surfaceCadenceMul = 1;

  private constructor(root: THREE.Object3D, mixer: THREE.AnimationMixer) {
    this.root = root;
    this.mixer = mixer;
  }

  /**
   * ── ⭐ P1 · LOAD THE CHARACTER FROM THE ASSET LIBRARY, IN THE BOOT LANE ────────────────────────
   *
   * What changed, and what deliberately did not.
   *
   * CHANGED — the source. Twelve literal URLs became one body id and eleven clip ids, resolved
   * through the catalog to content-hashed, immutably-cached paths with a byte budget and a dispose
   * path. The BOOT lane is what makes them hold `playing` (`world/readiness.worldPlayable`).
   *
   * CHANGED — the failure policy, strictly for the better. `Promise.all` over twelve GLBs meant ONE
   * 404 cost the whole character (research 02 §1.1; the Axon rig was deliberately built not to share
   * this shape — plan D25). Every `handle.ready` here RESOLVES, never rejects, so a dead clip is now
   * one missing action and one warning, and only a dead BODY costs the character. That rejection is
   * the one this function still has, and it is what keeps the capsule.
   *
   * ⚠️ CHANGED, AND LOAD-BEARING — THE CLIPS ARE CLONED BEFORE SURGERY. `surgeonHipsTrack` REWRITES
   * `Hips.position` values IN PLACE, and a library clip source is SHARED across every handle that
   * acquires it — that is the whole point of a refcounted source. Surgery on the shared curves would
   * be applied a second time to the second `CharacterView` built in a session: silently,
   * cumulatively, and invisibly to every test that builds one. `AnimationClip.clone()` deep-copies
   * the tracks, so the curve data is per-view and freed with the view.
   *
   * UNCHANGED — everything the animation layer does. `PLAYER_GRAPH`, `clipMeta.json`, phase
   * ownership, the one-shot trims, the parity audit, the reference guard, `PoseController.bind`,
   * and `charFix`/`scale 1.22`/`HIP_Y` over in `scene.ts`. Reading those out of the body's
   * `rig.json` — which now carries them — is P2's; this stage moved the BYTES, not the rig.
   */
  static async load(
    assets: AssetService,
    ids: { body?: string; clips?: Record<string, string> } = {},
    // TEMPORARY skin recolor (`?skin=` in mount.ts) — see SKIN_PALETTE there for why this is the
    // only "outfit" the current single untextured mesh can honestly support, and why it is not
    // the eventual cosmetics system (CosmeticReward in net/axon/model.ts). Undefined ⇒ the
    // library's own fallback color, unchanged.
    colorOverride?: number,
  ): Promise<CharacterView> {
    const bodyId = ids.body ?? PLAYER_BODY_ASSET;
    const clipIds = ids.clips ?? CLIP_ASSETS;
    const clipNames = Object.keys(clipIds);

    // Acquire SYNCHRONOUSLY, all of them, before the first await: the caller always has something
    // to hold and release whatever the network does, and the boot gate sees them immediately.
    const bodyHandle = assets.acquire(bodyId, { lane: 'boot', kind: 'body' });
    const clipHandles = clipNames.map((n) => assets.acquire(clipIds[n]!, { lane: 'boot', kind: 'clip' }));

    const [bodyLoaded] = await Promise.all([bodyHandle.ready, ...clipHandles.map((h) => h.ready)]);

    const releaseAll = (): void => {
      bodyHandle.release();
      for (const h of clipHandles) h.release();
    };

    const instance = bodyLoaded.ok ? bodyHandle.instance() : null;
    if (instance === null) {
      releaseAll();
      throw new Error(
        `library body '${bodyId}' unavailable (${bodyLoaded.failure?.code ?? 'unknown'}: ` +
          `${bodyLoaded.failure?.message ?? 'no instance'})`,
      );
    }

    const body = instance.root;
    /** Materials this view MINTED (the teal fallback). The SOURCE's own belong to the service. */
    const owned: THREE.Material[] = [];
    body.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh) {
        // Shadow flags (cast AND receive — the owner's "he doesn't receive shadows") arrive from
        // `SHADOW_FLAGS.player` through the player group's `childadded` hook when this root is
        // parented under `rollSpin` (B3, `render/lights/shadowFlags.ts`). Not written here.
        mesh.frustumCulled = false;
        const std = mesh.material as THREE.MeshStandardMaterial;
        if (!std.map) {
          const mat = new THREE.MeshStandardMaterial({
            // TEMPORARY skin recolor (`?skin=` in mount.ts) — see SKIN_PALETTE there for why this
            // is the only "outfit" the current single untextured mesh can honestly support, and
            // why it is not the eventual cosmetics system (CosmeticReward in net/axon/model.ts).
            // Undefined ⇒ the library's own fallback color, byte-identical to before this existed.
            color: colorOverride ?? 0x69c6d0,
            roughness: 0.55,
            side: THREE.DoubleSide,
          });
          mesh.material = mat;
          owned.push(mat);
        } else {
          std.side = THREE.DoubleSide;
        }
      }
    });

    const mixer = new THREE.AnimationMixer(body);
    const view = new CharacterView(body, mixer);
    view.handles = [bodyHandle, ...clipHandles];
    view.owned = owned;

    // The body's own sidecar decides whether the clips' armature root needs remapping onto this
    // body's spelling. `null` — no sidecar, no mismatch, or an ambiguous one — leaves every track
    // exactly as the pack wrote it.
    const rootMap = skeletonRootMap(body, instance.rig);
    let remapped = 0;

    // Track-parity audit (guard 1.8): every clip should be fully baked at the same track set.
    const trackCounts = new Set<number>();
    clipNames.forEach((name, i) => {
      const source = clipHandles[i]!.instance()?.[0];
      if (!source) {
        console.warn(
          `[anim] library clip '${clipIds[name]!}' carries no animation — '${name}' will not play`,
        );
        return;
      }
      // SEE THE HEADER: the source is SHARED and `surgeonHipsTrack` mutates, so surgery runs on a copy.
      const clip = source.clone();
      trackCounts.add(clip.tracks.length);
      if (rootMap !== null) remapped += remapSkeletonRoot(clip, rootMap);
      surgeonHipsTrack(clip, name); // BEFORE clipAction — bindings cache there
      const action = mixer.clipAction(clip);
      const meta = metaFor(name);
      if (meta?.oneShot) {
        action.setLoop(THREE.LoopOnce, 1);
        action.clampWhenFinished = true;
      }
      action.play();
      action.setEffectiveWeight(0);
      view.actions.set(name, action);
      view.durations.set(name, clip.duration);
    });
    if (trackCounts.size > 1) {
      console.warn(`[anim] track-count mismatch across clips: ${[...trackCounts].join(', ')}`);
    }
    if (rootMap !== null && remapped > 0) {
      console.info(
        `[anim] bound ${remapped} clip track(s) from '${rootMap.clip}.*' onto the body's ` +
          `'${rootMap.body}.*' (rig.json's skeletonRoot/clipSkeletonRoot map) — these are the ` +
          `tracks that used to log "PropertyBinding: No target node found" on every boot.`,
      );
    }

    // Guard: every clip the graph/slots reference must exist (a miss silently leaks bind pose).
    const referenced = [
      'idle', 'walk', 'run', 'run_boost', 'rise', 'jump_run_start',
      'fall_loop', 'fall_run', 'float', 'land', 'land_run',
    ];
    for (const name of referenced) {
      if (!view.actions.has(name)) console.warn(`[anim] graph references missing clip "${name}"`);
    }

    view.pose.bind(body);
    return view;
  }

  /**
   * ── ⭐ P1 · THE TEARDOWN THAT DID NOT EXIST (research 02 §2) ───────────────────────────────────
   *
   * Before this there was no `unload`/`dispose` on `CharacterView` at all, so a body swap meant
   * constructing a second view — which re-fetched all twelve GLBs, because nothing cached — and
   * leaking the first. `AxonRig.dispose()` has always done this properly; this is that pattern,
   * with one difference the library forces.
   *
   * ⚠️ IT DOES NOT DISPOSE GEOMETRY OR THE SOURCE'S MATERIALS, AND MUST NOT. The body root is a
   * `SkeletonUtils.clone` of a SHARED source: geometry and the glTF's own materials belong to the
   * library, are refcounted at the source, and are freed by the service's disposal walk after the
   * grace period. Freeing them here would draw from freed buffers in any other instance — the
   * `Destroyed texture … used in a submit` class. What this view OWNS is the teal fallback material
   * it minted per mesh and its own mixer bindings; that is what it frees, and `release()` is how it
   * tells the service the rest is no longer needed.
   *
   * Idempotent, like every dispose in this repo.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.root);
    this.root.removeFromParent();
    for (const mat of this.owned) mat.dispose();
    this.owned = [];
    this.actions.clear();
    this.durations.clear();
    this.prevWeights.clear();
    for (const h of this.handles) h.release();
    this.handles = [];
  }

  /** Per render frame: events → graph + slot → bridge semantics → mixer → pose layer. */
  update(
    inputs: AnimParams,
    vel: { x: number; z: number },
    facingYaw: number,
    dt: number,
    events: FeedbackEvent[],
  ): void {
    // Landing one-shot slot (montage equivalent); running landings get the running clip.
    // Suppressed while rolling — the roll owns the land clip as its tuck pose.
    for (const e of events) {
      if (e.type === 'land' && e.intensity > 0.35 && !inputs.rolling) {
        const clip = inputs.speed > 3.2 && this.actions.has('land_run') ? 'land_run' : 'land';
        slotPlay(this.slot, { clip, fadeIn: 0.04, fadeOut: 0.15, duration: 0.45 });
      }
    }

    const tasks = compose(evaluate(PLAYER_GRAPH, this.graphRt, inputs, dt), slotStep(this.slot, dt));

    // Aggregate weights per clip (a clip may appear in both base and slot).
    const weights = new Map<string, number>();
    const taskScale = new Map<string, number>();
    for (const t of tasks) {
      weights.set(t.clip, (weights.get(t.clip) ?? 0) + t.weight);
      taskScale.set(t.clip, t.timeScale);
    }

    // Locomotion phase: blended stride from contributing clips, one shared clock.
    const contribs: { weight: number; strideLengthM: number }[] = [];
    for (const [clip, w] of weights) {
      const meta = metaFor(clip);
      if (w > 1e-4 && meta?.syncGroup === 'locomotion' && meta.strideLengthM) {
        contribs.push({ weight: w, strideLengthM: meta.strideLengthM * this.strideScale });
      }
    }
    const hz = cycleHz(inputs.speed, blendedStride(contribs), this.maxCycleHz, this.surfaceCadenceMul);
    this.locomotionPhase = advancePhase(this.locomotionPhase, hz, dt);

    for (const [name, action] of this.actions) {
      const w = weights.get(name) ?? 0;
      const prev = this.prevWeights.get(name) ?? 0;
      const meta = metaFor(name);
      const rollingTuck = inputs.rolling && name === ROLL_TUCK_CLIP;

      // One-shot entry: restart from the trim point (skips baked anticipation/airborne head).
      if (meta?.oneShot && w > 1e-4 && prev <= 1e-4 && !rollingTuck) {
        action.reset();
        action.time = meta.trimStartS ?? 0;
      }

      action.setEffectiveWeight(w);
      this.prevWeights.set(name, w);
      if (w <= 1e-4) continue;

      if (rollingTuck) {
        // Dive → tuck → recover timeline under the procedural wrapper spin.
        action.setEffectiveTimeScale(0);
        action.time = rollTuckTime(inputs.rollProgress01);
      } else if (meta?.syncGroup === 'locomotion') {
        // Phase-owned: we write .time; timeScale 0 keeps the action un-paused but clock-free.
        action.setEffectiveTimeScale(0);
        const dur = this.durations.get(name)!;
        action.time = clipTime01(this.locomotionPhase, meta.phaseOffset01 ?? 0) * dur;
      } else if (name === 'rise' || name === 'jump_run_start') {
        // Fit the (trimmed) rise clip to the physics rise so clip apex == physics apex;
        // clampWhenFinished holds full extension through apex-hang.
        const dur = this.durations.get(name)! - (meta?.trimStartS ?? 0);
        const scale = inputs.jumpTimeToApex > 0.01 ? dur / inputs.jumpTimeToApex : 1;
        action.setEffectiveTimeScale(scale);
      } else {
        action.setEffectiveTimeScale(taskScale.get(name) ?? 1);
      }
    }

    this.mixer.update(dt);
    this.pose.apply(vel, facingYaw, dt, inputs.rolling);
  }
}
